import { EMOJI_CATALOGUE } from '@liniya/client-core';
import { useEffect, useRef, useState } from 'react';

interface Props {
  onPick: (emoji: string) => void;
  onClose: () => void;
}

/**
 * The whole Unicode catalogue, grouped as the standard groups it.
 *
 * It used to be twenty-four hand-picked emoji. The phone now offers the full
 * set, and a picker that differs between the two clients means a reaction sent
 * from one has no button in the other.
 *
 * Every group is in one scrolling column with its heading inline, so the
 * category strip can jump to one without stopping anyone from simply
 * scrolling past it.
 */
export function EmojiPicker({ onPick, onClose }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const jumpTo = (index: number) => {
    setActive(index);
    const heading = scroller.current?.querySelector<HTMLElement>(`[data-group="${index}"]`);
    heading?.scrollIntoView({ block: 'start' });
  };

  return (
    <div
      ref={panel}
      className="emoji-picker"
      role="dialog"
      aria-label="Выбор эмодзи"
    >
      <div className="emoji-tabs" role="tablist">
        {EMOJI_CATALOGUE.map((group, index) => (
          <button
            key={group.label}
            type="button"
            role="tab"
            aria-selected={index === active}
            className={index === active ? 'active' : undefined}
            onClick={() => jumpTo(index)}
          >
            {group.label}
          </button>
        ))}
      </div>

      <div className="emoji-scroll" ref={scroller}>
        {EMOJI_CATALOGUE.map((group, index) => (
          <section key={group.label} data-group={index}>
            <h3>{group.label}</h3>
            <div className="emoji-grid">
              {group.emoji.map((emoji) => (
                <button key={emoji} type="button" onClick={() => onPick(emoji)} aria-label={emoji}>
                  {emoji}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
